import React, { useState, useEffect, useRef, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MoreHorizontal } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';
import { useOptimizedNavigationMenu } from '@/hooks/useOptimizedNavigationMenu';
import NavigationSkeleton from './NavigationSkeleton';
import { useScreenSize } from '@/hooks/use-mobile';
import { featureFlags } from '@/utils/featureFlags';
import { useNavOverflow } from '@/hooks/useNavOverflow';

interface ResponsiveNavigationSystemProps {
  variant?: 'home' | 'internal' | 'sticky';
  isScrolled?: boolean;
  isDarkSection?: boolean;
  className?: string;
}

interface NavigationItem {
  label: string;
  href: string;
}

const ResponsiveNavigationSystem: React.FC<ResponsiveNavigationSystemProps> = ({
  variant = 'internal',
  isScrolled = false,
  isDarkSection = false,
  className = ''
}) => {
  const { language } = useLanguage();
  const { isMobile, isTablet, isMobileOrTablet } = useScreenSize();
  const location = useLocation();

  const [showMoreDropdown, setShowMoreDropdown] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Fetch navigation from database
  const { data: navigationMenu, isLoading, error } = useOptimizedNavigationMenu('main-header');

  // Transform navigation items (preserve DB order exactly)
  const allNavigationItems: NavigationItem[] = useMemo(() => {
    if (!navigationMenu?.items) return [];
    
    return navigationMenu.items.map(item => ({
      label: language === 'ar' ? item.title_ar : item.title_en,
      href: item.external_url || (item.slug ? `/${String(item.slug).replace(/^\/+/, '')}` : '#')
    }));
  }, [navigationMenu?.items, language]);
  const orderedItems = allNavigationItems;

  // Decide whether to use overflow system
  const overflowEnabled = featureFlags.ff_nav_overflow_menu;

  const { containerRef, registerMeasureEl, moreRef, visible, hidden, recompute } = useNavOverflow(
    overflowEnabled ? orderedItems : orderedItems,
    { gap: 8, reserveMoreMin: 44 }
  );

  // Heuristic: if only 1 item visible but container still has >60px free, force recompute with smaller reserve to try fit second item
  useEffect(() => {
    if (!overflowEnabled) return;
    if (visible.length === 1 && hidden.length >= 1 && containerRef.current) {
      const free = containerRef.current.offsetWidth - containerRef.current.scrollWidth;
      if (free > 60) {
        // Temporarily reduce more button width by adding a class shrink (handled via inline style just for measure span)
        if (moreRef.current) {
          const originalWidth = moreRef.current.style.width;
          moreRef.current.style.width = '28px';
          requestAnimationFrame(() => {
            recompute();
            // restore after measurement
            setTimeout(() => { moreRef.current && (moreRef.current.style.width = originalWidth); }, 50);
          });
        }
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible.length, hidden.length, overflowEnabled, recompute]);

  // Recompute when visual styling that can affect width changes
  useEffect(() => {
    if (!overflowEnabled) return;
    recompute();
    const phases = [120, 280, 520];
    const ids = phases.map(ms => window.setTimeout(() => recompute(), ms));
    return () => ids.forEach(id => clearTimeout(id));
  }, [overflowEnabled, isScrolled, isDarkSection, location.pathname, language, variant, orderedItems, recompute]);

  // Recompute after fonts load (important for Arabic / custom fonts)
  useEffect(() => {
    if (!overflowEnabled) return;
  const docWithFonts = document as unknown as { fonts?: { ready?: Promise<unknown> } };
  const fontsReady = docWithFonts.fonts?.ready;
    if (fontsReady && typeof fontsReady.then === 'function') {
      fontsReady.then(() => recompute()).catch(() => {});
    }
  }, [overflowEnabled, orderedItems, recompute]);

  // When feature disabled, treat all as visible
  const visibleItems = overflowEnabled ? visible : orderedItems;
  const hiddenItems = overflowEnabled ? hidden : [];

  // Handle outside click for dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowMoreDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const isActivePath = (path: string): boolean => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  const getLinkClasses = (href: string) => {
    const isActive = isActivePath(href);
    const baseClasses = `px-4 py-2 rounded-lg text-sm font-medium transition-all duration-300 whitespace-nowrap relative overflow-hidden ${language === 'ar' ? 'font-arabic-body' : 'font-english-body'}`;
    
    if (variant === 'internal') {
      return `${baseClasses} ${isActive 
        ? 'bg-white/20 text-white border border-white/30 backdrop-blur-sm' 
        : 'text-white/90 hover:text-white hover:bg-white/10 backdrop-blur-sm'
      }`;
    }
    
    if (variant === 'home' && !isScrolled && isDarkSection) {
      return `${baseClasses} ${isActive 
        ? 'bg-white/20 text-white border border-white/30 backdrop-blur-sm' 
        : 'text-white/90 hover:text-white hover:bg-white/10 backdrop-blur-sm'
      }`;
    }
    
    return `${baseClasses} ${isActive 
      ? 'bg-primary text-primary-foreground' 
      : 'text-foreground hover:text-primary hover:bg-accent/50'
    }`;
  };

  const getDropdownClasses = () => {
    const baseClasses = "absolute top-full right-0 rtl:right-auto rtl:left-0 mt-2 w-48 bg-background/95 backdrop-blur-md rounded-lg shadow-elegant border border-border py-1 z-50";
    return baseClasses;
  };

  // Don't render on mobile - mobile sidebar handles navigation
  if (isMobileOrTablet) {
    return null;
  }

  return (
    <nav 
      ref={containerRef} 
      className={`hidden lg:flex items-center space-x-2 rtl:space-x-reverse flex-nowrap ${className}`}
    >
      {/* Loading State */}
      {isLoading ? (
        <NavigationSkeleton 
          variant={variant}
          isDarkSection={isDarkSection}
          isScrolled={isScrolled}
          itemCount={6}
        />
      ) : error ? (
        <div className="px-4 py-2 text-sm text-muted-foreground">
          {language === 'ar' ? 'خطأ في تحميل القائمة' : 'Navigation Error'}
        </div>
      ) : (
        <>
          {/* Hidden measurement container */}
          {overflowEnabled && (
            <div className="absolute -left-[9999px] top-auto opacity-0 pointer-events-none" aria-hidden="true">
              {orderedItems.map((item, index) => (
                <span
                  key={`measure-${item.href}-${index}`}
                  ref={registerMeasureEl(index)}
                  data-measure="true"
                  className={getLinkClasses(item.href)}
                >
                  {item.label}
                </span>
              ))}
              <span
                ref={moreRef}
                className={getLinkClasses('/__more__')}
              >
                ...
              </span>
            </div>
          )}

          {/* Visible Navigation Items */}
          {visibleItems.map((item, index) => (
            <motion.div
              key={`${item.href}-${index}`}
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: index * 0.05 }}
            >
              <Link
                to={item.href}
                className={getLinkClasses(item.href)}
              >
                <span className={language === 'ar' ? 'font-arabic-body' : 'font-english-body'}>
                  {item.label}
                </span>
                {isActivePath(item.href) && (
                  <motion.div
                    className={`absolute bottom-0 left-0 right-0 h-0.5 ${
                      variant === 'home' && !isScrolled && isDarkSection ? 'bg-white' : 'bg-primary'
                    }`}
                    layoutId="activeNavTab"
                    transition={{ duration: 0.3 }}
                  />
                )}
              </Link>
            </motion.div>
          ))}

          {/* More Dropdown for Hidden Items */}
      {hiddenItems.length > 0 && overflowEnabled && (
            <div className="relative" ref={dropdownRef}>
              <motion.button
                onClick={() => setShowMoreDropdown(!showMoreDropdown)}
                className={`flex items-center px-4 py-2 text-sm font-medium rounded-lg transition-all duration-300 group ${
                  variant === 'home' && !isScrolled && isDarkSection
                    ? 'text-white/90 hover:text-white hover:bg-white/10'
                    : 'text-foreground hover:text-primary hover:bg-accent/50'
                }`}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                aria-label={language === 'ar' ? 'المزيد' : 'More'}
        aria-haspopup="menu"
        aria-expanded={showMoreDropdown}
              >
                <span className={`leading-none ${language === 'ar' ? 'font-arabic-body' : 'font-english-body'}`}>...</span>
              </motion.button>

              <AnimatePresence>
                {showMoreDropdown && (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.95, y: -10 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.95, y: -10 }}
                    transition={{ duration: 0.2 }}
                    className={getDropdownClasses()}
                  >
        {hiddenItems.map((item, index) => (
                      <motion.div
                        key={`dropdown-${item.href}-${index}`}
                        initial={{ opacity: 0, x: -10 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.2, delay: index * 0.03 }}
                      >
                        <Link
                          to={item.href}
                          className={`block px-4 py-3 text-sm transition-colors group ${
                            isActivePath(item.href) 
                              ? 'bg-primary text-primary-foreground' 
                              : 'text-foreground hover:bg-accent hover:text-primary'
                          } ${language === 'ar' ? 'font-arabic-body text-right' : 'font-english-body text-left'}`}
                          onClick={() => setShowMoreDropdown(false)}
          role="menuitem"
                        >
                          <span className="relative">
                            {item.label}
                            {isActivePath(item.href) && (
                              <motion.div
                                className="absolute left-0 top-0 bottom-0 w-1 bg-primary-foreground rounded-r"
                                layoutId="dropdownActive"
                                transition={{ duration: 0.3 }}
                              />
                            )}
                          </span>
                        </Link>
                      </motion.div>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          )}
        </>
      )}
    </nav>
  );
};

export default ResponsiveNavigationSystem;